/** sessionStorage key — cleared on successful bootstrap in `main.ts`. */
export const CHUNK_RELOAD_GUARD_KEY = 'remmi:chunk-reload-attempted';

const CHUNK_LOAD_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Loading chunk [\d]+ failed/i,
  /ChunkLoadError/i,
];

/** Detect stale lazy-route chunks after a new build or dev-server rebuild. */
export function isChunkLoadError(error: unknown): boolean {
  const haystack = collectErrorText(error);
  return CHUNK_LOAD_PATTERNS.some((pattern) => pattern.test(haystack));
}

/**
 * Reload once when a lazy chunk is missing. Returns `true` if a reload was triggered.
 * A second failure in the same session is logged normally to avoid reload loops.
 */
export function reloadOnceForStaleChunk(): boolean {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return false;
  }
  if (sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY)) {
    sessionStorage.removeItem(CHUNK_RELOAD_GUARD_KEY);
    return false;
  }
  sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, String(Date.now()));
  window.location.reload();
  return true;
}

export function clearChunkReloadGuard(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.removeItem(CHUNK_RELOAD_GUARD_KEY);
}

function collectErrorText(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }
  if (error instanceof Error) {
    return [error.name, error.message, error.stack ?? ''].join(' ');
  }
  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>;
    const parts = [
      record['message'],
      record['name'],
      record['stack'],
    ]
      .filter((part): part is string => typeof part === 'string')
      .join(' ');
    if (parts) {
      return parts;
    }
  }
  return String(error);
}
